/* import __COLOCATED_TEMPLATE__ from './freshdesk.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */

import type AppService from 'embercom/services/app-service';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { type SyncSteps } from 'embercom/components/knowledge-hub/filterable-list';
import type ImportSource from 'embercom/models/import-service/import-source';
import type IntlService from 'embercom/services/intl';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import { SourceType, SyncBehaviorType } from 'embercom/models/import-service/import-source';

interface Args {
  modal: any;
  title?: string;
  onNavigationBack?: () => void;
  onNavigationNext?: (viewId: string) => void;
  showBackButton?: boolean;
  onModalClose: () => void;
  setSyncStep: (step: SyncSteps) => void;
  isReauthenticating?: boolean;
  isReconfiguring?: boolean;
  existingImportSource?: ImportSource;
}

export default class FreshdeskSyncModal extends Component<Args> {
  @service declare knowledgeHubService: KnowledgeHubService;
  @service declare intl: IntlService;
  @service declare notificationsService: any;
  @service declare intercomEventService: any;
  @service declare appService: AppService;

  @tracked importSource: ImportSource | undefined;
  @tracked hasCredentials = false;
  @tracked apiKey = '';
  @tracked domain = '';
  @tracked syncBehavior: SyncBehaviorType = SyncBehaviorType.Sync;
  @tracked existingImportSource: ImportSource | undefined;
  @tracked isReauthenticating = false;
  @tracked isReconfiguring = false;
  @tracked sourceType = SourceType[SourceType.Freshdesk];

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    if (args.isReauthenticating && args.existingImportSource) {
      this.existingImportSource = args.existingImportSource;
      this.isReauthenticating = true;
      // Pre-fill domain from existing source
      if (this.existingImportSource.context?.domain) {
        this.domain = this.existingImportSource.context.domain;
      }
    }
  }

  get importSourceId() {
    return this.importSource?.id;
  }

  get submitDisabled() {
    return this.apiKey.length === 0 || this.domain.length === 0;
  }

  @action
  async submit() {
    if (this.args.isReauthenticating) {
      await this.startReauthenticating();
    } else {
      await this.startSync();
    }
  }

  async startSync() {
    let importSourceParams = {
      sourceType: SourceType.Freshdesk,
      authToken: this.apiKey,
      domain: this.domain,
    };

    try {
      this.importSource = await this.knowledgeHubService.createImportSource(importSourceParams);

      await this.knowledgeHubService.startImport(this.importSource.id, this.syncBehavior);

      this.knowledgeHubService.notifySourceStateChangeConfirmation(
        this.intl.t('knowledge-hub.sync-modal.freshdesk.sync-started'),
        this.importSource.id,
      );

      this.intercomEventService.trackAnalyticsEvent({
        action: 'clicked',
        context: 'import-content',
        object: `import-content.freshdesk`,
        section: `knowledge-hub.new-content.freshdesk`,
        place: 'knowledge-hub.new-content',
      });

      this.args.onModalClose();
    } catch (error: any) {
      if (error.jqXHR?.status === 409) {
        this.notificationsService.notifyError(
          this.intl.t('knowledge-hub.sync-modal.freshdesk.sync-already-in-progress'),
        );
      } else if (error.jqXHR?.status === 403) {
        this.notificationsService.notifyError(
          this.intl.t('knowledge-hub.sync-modal.freshdesk.invalid-credentials'),
        );
      } else {
        this.notificationsService.notifyError(
          this.intl.t('knowledge-hub.sync-modal.freshdesk.sync-error'),
        );
      }
    }
  }

  @action
  async startReauthenticating() {
    try {
      let importSource = this.args.existingImportSource;
      if (!importSource) {
        throw new Error('Import source not found');
      }

      importSource.authToken = this.apiKey;
      // Keep the existing domain from context

      await importSource.save();

      await this.knowledgeHubService.startImport(importSource.id, this.syncBehavior);

      this.notificationsService.notifyConfirmation(
        this.intl.t('knowledge-hub.sync-modal.freshdesk.re-authenticate-success'),
      );

      this.knowledgeHubService.notifySourceStateChangeConfirmation(
        this.intl.t('knowledge-hub.sync-modal.freshdesk.sync-started'),
        importSource.id,
      );

      this.intercomEventService.trackAnalyticsEvent({
        action: 'updated',
        object: 'freshdesk_credentials',
        section: 'knowledge-hub',
        place: 'freshdesk_modal',
      });

      this.args.onModalClose();
    } catch (error) {
      console.error('Error reauthenticating', error);
      this.notificationsService.notifyError(
        this.intl.t('knowledge-hub.sync-modal.freshdesk.re-authenticate-error'),
      );
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::SetupModal::SyncModal::Freshdesk': typeof FreshdeskSyncModal;
    'knowledge-hub/setup-modal/sync-modal/freshdesk': typeof FreshdeskSyncModal;
  }
}
